import { useState, useEffect } from "react";
import apiRequest, { API_URLS } from "../services/api";
import Alert from "../components/Alert";
import { Upload, FileText, X, Download, Eye, Trash2, Plus, Image as ImageIcon, AlertTriangle } from "lucide-react";

export default function Knowledge() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [alert, setAlert] = useState({ show: false, message: "", type: "" });
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    file: null,
    survey_id: ""
  });
  const [surveys, setSurveys] = useState([]);

  useEffect(() => {
    fetchDocuments();
    fetchSurveys();
  }, []);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await apiRequest(API_URLS.admin.getDocuments);
      setDocuments(response.data || []);
    } catch (error) {
      console.error("Error fetching documents:", error);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSurveys = async () => {
    try {
      const response = await apiRequest(API_URLS.admin.listSurveys);
      setSurveys(response.data || []);
    } catch (error) {
      console.error("Error fetching surveys:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {

      if (file.size > 10 * 1024 * 1024) {
        setAlert({
          show: true,
          message: "File size must be less than 10MB",
          type: "error"
        });
        return;
      }
      setFormData(prev => ({
        ...prev,
        file: file,
        name: prev.name || file.name.split('.')[0]
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.file) {
      setAlert({
        show: true,
        message: "Name and file are required",
        type: "error"
      });
      return;
    }

    try {
      setUploading(true);
      
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('description', formData.description || '');
      formDataToSend.append('file', formData.file);
      if (formData.survey_id) {
        formDataToSend.append('survey_id', formData.survey_id);
      }

      const response = await fetch(API_URLS.admin.uploadDocument, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: formDataToSend
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Upload failed');
      }

      setAlert({
        show: true,
        message: "Document uploaded successfully!",
        type: "success"
      });

      // Reset form
      setFormData({
        name: "",
        description: "",
        file: null,
        survey_id: ""
      });
      setShowUploadModal(false);
      
      // Refresh documents list
      fetchDocuments();
      
    } catch (error) {
      setAlert({
        show: true,
        message: error.message || "Failed to upload document",
        type: "error"
      });
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (fileType) => {
    const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
    if (imageTypes.includes(fileType?.toLowerCase())) {
      return <ImageIcon className="w-8 h-8 text-green-500" />;
    }
    return <FileText className="w-8 h-8 text-blue-500" />;
  };

  const isImageFile = (fileType) => {
    const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
    return imageTypes.includes(fileType?.toLowerCase());
  };

  const handleImageClick = (document) => {
    if (isImageFile(document.file_type) && document.url) {
      setSelectedImage(document);
      setShowImageModal(true);
    }
  };

  const handleDeleteDocument = async () => {
    if (!documentToDelete) return;
    
    try {
      await apiRequest(API_URLS.admin.deleteDocument(documentToDelete), 'DELETE');
      
      setAlert({
        show: true,
        message: "Document deleted successfully!",
        type: "success"
      });

      setShowDeleteConfirm(false);
      setDocumentToDelete(null);
      
      // Refresh documents list
      fetchDocuments();
      
    } catch (error) {
      setAlert({
        show: true,
        message: error.message || "Failed to delete document",
        type: "error"
      });
    }
  };

  const documentToDeleteName = documents.find(d => d.id === documentToDelete)?.name;

  const confirmDeleteDocument = (documentId) => {
    setDocumentToDelete(documentId);
    setShowDeleteConfirm(true);
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Knowledge Base</h1>
        <p className="text-gray-600">Upload and manage reference documents and materials</p>
      </div>

      {alert.show && (
        <Alert
          message={alert.message}
          type={alert.type}
          onClose={() => setAlert({ show: false, message: "", type: "" })}
        />
      )}

      {/* Upload Button */}
      <div className="mb-6">
        <button
          onClick={() => setShowUploadModal(true)}
          className="flex items-center gap-2 bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition-colors"
        >
          <Plus size={20} />
          Upload Document
        </button>
      </div>

      {/* Documents Grid */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
        </div>
      ) : documents.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No documents yet</h3>
          <p className="text-gray-500 mb-4">Upload your first document to get started</p>
          <button
            onClick={() => setShowUploadModal(true)}
            className="inline-flex items-center gap-2 bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition-colors"
          >
            <Upload size={16} />
            Upload Document
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {documents.map((doc) => (
            <div key={doc.id} className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div 
                  className={isImageFile(doc.file_type) ? "cursor-pointer hover:opacity-80 transition-opacity" : ""}
                  onClick={() => handleImageClick(doc)}
                >
                  {getFileIcon(doc.file_type)}
                </div>
                <div className="flex gap-1">
                  <button
                    className="p-1 text-gray-500 hover:text-blue-600 transition-colors"
                    title="View"
                    onClick={() => isImageFile(doc.file_type) ? handleImageClick(doc) : window.open(doc.url, '_blank')}
                  >
                    <Eye size={16} />
                  </button>
                  <button
                    className="p-1 text-gray-500 hover:text-green-600 transition-colors"
                    title="Download"
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = doc.url;
                      link.download = doc.name;
                      link.target = '_blank';
                      link.click();
                    }}
                  >
                    <Download size={16} />
                  </button>
                  <button
                    className="p-1 text-gray-500 hover:text-red-600 transition-colors"
                    title="Delete"
                    onClick={() => confirmDeleteDocument(doc.id)}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              <h3 className="font-medium text-gray-900 mb-1 truncate">{doc.name}</h3>
              {doc.description && (
                <p className="text-sm text-gray-600 mb-2 line-clamp-2">{doc.description}</p>
              )}
              {isImageFile(doc.file_type) && (
                <div className="mb-2">
                  <img 
                    src={doc.url} 
                    alt={doc.name}
                    className="w-full h-32 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={() => handleImageClick(doc)}
                    loading="lazy"
                  />
                </div>
              )}
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>{doc.file_type?.toUpperCase()}</span>
                <span>{formatFileSize(doc.file_size)}</span>
              </div>
              {doc.url && (
                <div className="mt-2">
                  <a
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:text-blue-800 truncate block"
                    title={doc.url}
                  >
                    View File
                  </a>
                </div>
              )}
              <div className="mt-1 text-xs text-gray-400">
                {new Date(doc.created_at).toLocaleDateString()}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
            {/* Icon + title */}
            <div className="flex flex-col items-center text-center px-6 pt-8 pb-5">
              <div className="w-14 h-14 bg-red-50 rounded-2xl flex items-center justify-center mb-4 ring-8 ring-red-50">
                <AlertTriangle className="w-7 h-7 text-red-500" />
              </div>
              <h2 className="text-lg font-bold text-slate-900">Delete Document</h2>
              <p className="text-sm text-slate-500 mt-2 leading-relaxed">
                Are you sure you want to delete{" "}
                <span className="font-semibold text-slate-800">"{documentToDeleteName}"</span>?
                This will permanently remove the document and cannot be undone.
              </p>
            </div>

            {/* Buttons */}
            <div className="flex gap-3 px-6 pb-6">
              <button
                onClick={handleDeleteDocument}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-xl transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Delete Document
              </button>
              <button
                onClick={() => { setShowDeleteConfirm(false); setDocumentToDelete(null); }}
                className="flex-1 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold rounded-xl transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {showImageModal && selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
          <div className="relative max-w-5xl max-h-[90vh] w-full">
            <button
              onClick={() => setShowImageModal(false)}
              className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
            >
              <X size={24} />
            </button>
            <img 
              src={selectedImage.url} 
              alt={selectedImage.name}
              className="w-full h-full object-contain rounded-lg"
            />
            <div className="absolute bottom-4 left-4 right-4 bg-black bg-opacity-75 text-white p-4 rounded-lg">
              <h3 className="text-lg font-semibold mb-1">{selectedImage.name}</h3>
              {selectedImage.description && (
                <p className="text-sm text-gray-300 mb-2">{selectedImage.description}</p>
              )}
              <div className="flex items-center justify-between text-xs text-gray-400">
                <span>{selectedImage.file_type?.toUpperCase()}</span>
                <span>{formatFileSize(selectedImage.file_size)}</span>
                <span>{new Date(selectedImage.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-semibold text-gray-900">Upload Document</h2>
              <button
                onClick={() => setShowUploadModal(false)}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Document Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Enter document name"
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Enter document description (optional)"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  File *
                </label>
                <input
                  type="file"
                  onChange={handleFileChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Maximum file size: 10MB</p>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Link to Survey (Optional)
                </label>
                <select
                  name="survey_id"
                  value={formData.survey_id}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="">Select a survey (optional)</option>
                  {surveys.map((survey) => (
                    <option key={survey.id} value={survey.id}>
                      {survey.title}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowUploadModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading}
                  className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploading ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
